/**
 * Highlight Component
 * Renders a highlight overlay on the PDF page at the comment's position.
 */
import React from 'react';
import type { Position, ConfidenceLevel, CommentStatus } from '../../types';

interface HighlightProps {
  position: Position;
  confidence: ConfidenceLevel;
  status?: CommentStatus;
  isSelected?: boolean;
  onClick?: () => void;
  pageWidth: number;
  pageHeight: number;
}

// Colors for pending comments - always yellow to indicate awaiting review
const confidenceColors = {
  high: 'bg-yellow-300',
  medium: 'bg-yellow-300',
  low: 'bg-yellow-300',
};

// Colors for reviewed comments based on status
const statusColors = {
  pending: '', // Will use confidence colors
  accepted: 'bg-emerald-400',
  edited: 'bg-blue-400',
  rejected: 'bg-gray-300', // Shown faded if at all
};

const Highlight: React.FC<HighlightProps> = ({
  position,
  confidence,
  status = 'pending',
  isSelected = false,
  onClick,
}) => {
  // Convert normalized coordinates to absolute pixels
  const style: React.CSSProperties = {
    position: 'absolute',
    left: `${position.x * 100}%`,
    top: `${position.y * 100}%`,
    width: `${position.width * 100}%`,
    height: `${position.height * 100}%`,
    pointerEvents: 'auto',
    zIndex: 20,
  };

  // Determine background color based on status
  const bgColor =
    status === 'pending'
      ? confidenceColors[confidence]
      : statusColors[status] || confidenceColors[confidence];

  // Reviewed comments are less prominent (lower opacity)
  const baseOpacity = status === 'pending' ? 'opacity-30' : 'opacity-20';
  const hoverOpacity = status === 'pending' ? 'hover:opacity-50' : 'hover:opacity-40';

  return (
    <div
      className={`
        ${bgColor}
        ${isSelected ? 'opacity-50 ring-2 ring-blue-500' : baseOpacity}
        cursor-pointer transition-opacity ${hoverOpacity}
        rounded-sm
      `}
      style={style}
      onClick={onClick}
      role="button"
      aria-label={`Comment highlight - ${status} - ${confidence} confidence`}
    />
  );
};

export default Highlight;
